import { getNameOptionResponseFromFragmentName } from '@smx-ui-common/function';
import { murmurHashV3 } from '@smx-ui-common/murmurhashV3';
import { useFragmentFromStore } from '@smx-ui-model/hooks/use-fragment-from-store';
import { setStoreConnectionId } from '@smx-ui-model/store-model-slice';
import { TableResourceType } from '@smx-ui-model/types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyLoadQuery } from 'react-relay';
import { ArrayKeyType } from 'react-relay/relay-hooks/helpers';

interface TableEntityLoaderProps {
    model: any;
    variables?: any;
    setDataSource: React.Dispatch<React.SetStateAction<TableResourceType[] | undefined>>;
    setDataPageInfo: React.Dispatch<React.SetStateAction<unknown>>;
    fetchKey?: number;
    upAdditionalField?: string[];
}

const bufferFetchKey = new Set<number>();

export const WrapperTableLoaderQuery = ({
    model,
    variables,
    setDataSource,
    setDataPageInfo,
    fetchKey,
    upAdditionalField,
}: TableEntityLoaderProps) => {
    const dispatch = useDispatch();

    const newHashFetchKey = murmurHashV3(JSON.stringify(variables) + fetchKey);

    const queryResult = useLazyLoadQuery(model.query!, variables, {
        fetchPolicy: bufferFetchKey.has(newHashFetchKey) ? 'store-or-network' : 'store-and-network',
        fetchKey,
    }) as ArrayKeyType;

    bufferFetchKey.add(newHashFetchKey);

    const dataConnectionFragment = useFragmentFromStore(queryResult);

    const nameOptionResponse = getNameOptionResponseFromFragmentName(queryResult);
    const dataConnectionList = (dataConnectionFragment as any)?.[nameOptionResponse];

    const connectionId = dataConnectionList?.__id;

    const dataConnection = useFragmentFromStore(connectionId ? dataConnectionList : null);

    const dataPageInfo = useFragmentFromStore((dataConnection as any)?.pageInfo || null);

    useEffect(() => {
        setDataPageInfo(dataPageInfo);
    }, [dataPageInfo]);

    useEffect(() => {
        const getPrefix = (prefix: string) => {
            switch (prefix) {
                case 'projectId':
                    return variables.input.filter.projectId.value || '';
                default:
                    return '';
            }
        };

        dispatch(setStoreConnectionId({ [model.name + getPrefix(model.prefixConnection)]: connectionId }));
    }, [connectionId]);

    useEffect(() => {
        const dataSource = (dataConnection || dataConnectionList)?.edges.map((edge: any) => {
            const result = {
                key: edge.cursor,
                userRef: edge.node,
            } as any;

            upAdditionalField?.forEach((fieldName) => {
                result[fieldName] = edge.node.tags;
            });

            return result;
        });
        setDataSource(dataSource);
    }, [dataConnectionList]);

    return null;
};
